import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Lock, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Reveal, MaskLines } from "./Reveal";
import { LogoMark } from "./Logo";
import { useLang, SERVICES, GIFT_PRESETS, PACKAGES } from "../lib/i18n";
import { scrollToId } from "./Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackageCard = ({ p, lang, active, onPick }) => {
  const c = p[lang];
  const en = lang === "en";
  return (
    <div data-testid={`gift-package-${p.key}`} className={`relative border p-5 sm:p-6 transition-colors duration-300 ${active ? "border-[#C9A66B] bg-[#C9A66B]/10" : "border-[#F6F2EC]/15 bg-[#F6F2EC]/[0.03]"}`}>
      <span className="absolute -top-3 left-5 bg-[#C9A66B] text-[#14110F] text-[0.62rem] tracking-[0.2em] uppercase px-3 py-1">{en ? "Gift package" : "Pacchetto regalo"}</span>
      <div className="flex gap-5 items-start">
        <div className="relative w-24 sm:w-28 shrink-0 aspect-[4/5]">
          <img src={p.imgs[0]} alt="" className="absolute inset-0 w-full h-full object-cover rounded-t-full" />
          <img src={p.imgs[1]} alt="" className="absolute -right-3 -bottom-3 w-12 h-14 object-cover border-2 border-[#14110F]" />
        </div>
        <div className="min-w-0">
          <h3 className="font-serif-d font-light text-3xl sm:text-4xl leading-none" data-testid={`gift-package-name-${p.key}`}>{c.name}</h3>
          <ul className="mt-3 space-y-1 text-sm text-[#F6F2EC]">{c.items.map((x) => <li key={x} className="flex gap-2"><span className="text-[#C9A66B]">+</span>{x}</li>)}</ul>
          <p className="mt-3 text-sm text-[#d9d0c4] font-light leading-relaxed">{c.desc}</p>
          {p.page && <Link to={p.page} data-testid={`gift-package-page-${p.key}`} className="mt-3 inline-flex items-center gap-1 text-[0.68rem] tracking-[0.2em] uppercase text-[#C9A66B] underline underline-offset-4 hover:text-[#F6F2EC] transition-colors duration-300">{en ? "Discover more" : "Scopri di più"} <ArrowUpRight className="w-3.5 h-3.5" /></Link>}
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-[#d9d0c4]" data-testid={`gift-package-price-${p.key}`}>{p.price ? `€${p.price}` : en ? "Choose the amount below" : "Scegli tu l'importo qui sotto"}</span>
        <button type="button" onClick={onPick} data-testid={`gift-package-pick-${p.key}`}
          className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-300 ${active ? "bg-[#F6F2EC] text-[#14110F]" : "bg-[#C9A66B] text-[#14110F] hover:bg-[#F6F2EC]"}`}>
          {active ? (en ? "Selected" : "Selezionato") : en ? `Give the ${c.name}` : `Regala il ${c.name}`}
        </button>
      </div>
    </div>
  );
};

const GiftCardVisual = ({ amount, recipient, treatment, message, g }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 90, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 90, damping: 16 });
  const glare = useTransform(mx, [-0.5, 0.5], ["0%", "100%"]);
  const ref = useRef(null);
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div ref={ref} onMouseMove={move} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ perspective: 1200 }} className="w-full">
      <motion.div
        data-testid="gift-card-preview"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative aspect-[1.6/1] w-full rounded-2xl overflow-hidden bg-gradient-to-br from-[#2a231e] via-[#1a1613] to-[#0f0c0a] border border-[#C9A66B]/40 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]"
      >
        <motion.div className="absolute inset-0 pointer-events-none opacity-40" style={{ background: useTransform(glare, (v) => `radial-gradient(circle at ${v} 0%, rgba(201,166,107,0.45), transparent 55%)`) }} />
        <LogoMark className="absolute -right-10 -bottom-14 w-56 h-72 opacity-[0.12]" color="#F6F2EC" accent="#C9A66B" />
        <div className="relative h-full p-6 sm:p-8 flex flex-col justify-between text-[#F6F2EC]" style={{ transform: "translateZ(40px)" }}>
          <div className="flex items-start justify-between">
            <img src="/images/logo-gioia-light.png" alt="GIOIA centro estetico" className="h-9 sm:h-11 w-auto" />
            <span className="overline text-[0.6rem] text-[#C9A66B]">{g.card}</span>
          </div>
          <div>
            <motion.div key={amount} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="font-serif-d font-light text-6xl sm:text-7xl leading-none" data-testid="gift-card-amount">
              €{amount || "—"}
            </motion.div>
            <p className="mt-2 text-sm text-[#d9d0c4] truncate">{treatment || g.any}</p>
            {message && <p className="mt-1 font-serif-d italic text-[#e6d3ae] text-base truncate">“{message}”</p>}
          </div>
          <div className="flex items-end justify-between text-xs text-[#b8ad9f]">
            <span>{g.forLabel} <span className="text-[#F6F2EC]">{recipient || "—"}</span></span>
            <span>{g.valid}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Input = ({ label, ...p }) => (
  <label className="block">
    <span className="overline text-[0.62rem] text-[#b8ad9f]">{label}</span>
    <input {...p} className="w-full bg-transparent border-0 border-b border-[#F6F2EC]/25 py-3 text-[#F6F2EC] outline-none transition-colors duration-300 focus:border-[#C9A66B] placeholder:text-[#8b8278]" />
  </label>
);

export const GiftCards = () => {
  const { t, lang } = useLang();
  const g = t.gift;
  const [preset, setPreset] = useState("gift_100");
  const [custom, setCustom] = useState("");
  const [f, setF] = useState({ treatment: "", buyer_name: "", buyer_email: "", recipient_name: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const amount = preset === "custom" ? Number(custom) || 0 : GIFT_PRESETS.find(([k]) => k === preset)?.[1];
  const treatments = [...PACKAGES.map((p) => p[lang].label), ...SERVICES.flatMap((s) => s[lang].s)];
  const pickPackage = (p) => {
    setF({ ...f, treatment: p[lang].label });
    if (p.price) { setPreset("custom"); setCustom(String(p.price)); }
    scrollToId("gift-form");
  };
  const { state } = useLocation();
  useEffect(() => {
    const p = PACKAGES.find((x) => x.key === state?.giftPackage);
    if (!p) return undefined;
    const t = setTimeout(() => pickPackage(p), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const pay = async (e) => {
    e.preventDefault();
    if (preset === "custom" && (amount < 20 || amount > 1000)) { toast.error(g.invalid); return; }
    setLoading(true);
    try {
      const body = { ...f, treatment: f.treatment || null, message: f.message || null, lang, origin_url: window.location.origin,
        ...(preset === "custom" ? { custom_amount: Math.round(amount) } : { lookup_key: preset }) };
      const res = await axios.post(`${API}/payments/checkout`, body);
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
      else throw new Error("no url");
    } catch {
      toast.error(g.err);
      setLoading(false);
    }
  };

  const chip = (active) => `rounded-full px-5 py-2.5 text-sm border transition-colors duration-300 ${active ? "bg-[#C9A66B] border-[#C9A66B] text-[#14110F]" : "border-[#F6F2EC]/25 text-[#F6F2EC] hover:border-[#C9A66B]"}`;

  return (
    <section id="gift" data-testid="gift-section" className="relative bg-[#14110F] text-[#F6F2EC] py-24 sm:py-36 overflow-hidden grain">
      <div className="absolute -left-40 top-20 w-[50vw] h-[50vw] rounded-full bg-[#C9A66B]/10 blur-[120px] pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-14 lg:gap-12">
        <div className="lg:col-span-5">
          <Reveal><p className="overline text-[#C9A66B] mb-6">{g.over}</p></Reveal>
          <h2 className="font-serif-d font-light text-[2.6rem] sm:text-6xl leading-[0.98]">
            <MaskLines key={g.title[0]} inView lines={[g.title[0]]} />
            <MaskLines key={g.title[1]} inView delay={0.15} lines={[g.title[1]]} lineClass="italic text-[#C9A66B]" />
          </h2>
          <Reveal delay={0.1}><p className="mt-8 text-[#d9d0c4] font-light leading-relaxed max-w-md">{g.sub}</p></Reveal>
          <Reveal delay={0.15} className="mt-12 relative">
            <div className="relative overflow-hidden aspect-[4/5] w-[88%] rounded-t-[999px] bg-[#1d1916]" data-testid="gift-photo">
              <img src="/images/gift-card-gioia.jpg" alt={lang === "en" ? "Gioia gift card with bag and ribbon" : "Buono regalo Gioia con shopper e nastro"} className="absolute inset-0 w-full h-full object-cover object-[50%_60%]" loading="lazy" />
            </div>
            <div className="relative -mt-28 sm:-mt-36 ml-auto w-[82%]">
              <GiftCardVisual amount={amount} recipient={f.recipient_name} treatment={f.treatment} message={f.message} g={g} />
            </div>
          </Reveal>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/shop#kit-regalo" data-testid="gift-kit-link" className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.2em] uppercase text-[#C9A66B] underline underline-offset-8 hover:text-[#F6F2EC] transition-colors duration-300">
              {lang === "en" ? "Or give a Gioia Skincare gift kit" : "Oppure regala un kit Gioia Skincare"} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7 space-y-12">
          {PACKAGES.map((p) => <PackageCard key={p.key} p={p} lang={lang} active={f.treatment === p[lang].label} onPick={() => pickPackage(p)} />)}
          <form id="gift-form" onSubmit={pay} data-testid="gift-form" className="space-y-9 scroll-mt-28">
            <div>
              <p className="overline text-[0.62rem] text-[#b8ad9f] mb-4">{g.amount}</p>
              <div className="flex flex-wrap gap-3">
                {GIFT_PRESETS.map(([k, v]) => (
                  <button type="button" key={k} data-testid={`gift-amount-${v}`} onClick={() => setPreset(k)} className={chip(preset === k)}>€{v}</button>
                ))}
                <button type="button" data-testid="gift-amount-custom" onClick={() => setPreset("custom")} className={chip(preset === "custom")}>{g.other}</button>
              </div>
              {preset === "custom" && (
                <div className="mt-6 max-w-xs">
                  <Input label={g.custom} type="number" min={20} max={1000} required value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="gift-custom-input" />
                </div>
              )}
            </div>
            <label className="block">
              <span className="overline text-[0.62rem] text-[#b8ad9f]">{g.treatment}</span>
              <select data-testid="gift-treatment-select" value={f.treatment} onChange={set("treatment")} className="w-full bg-transparent border-0 border-b border-[#F6F2EC]/25 py-3 text-[#F6F2EC] outline-none focus:border-[#C9A66B] cursor-pointer">
                <option value="" className="text-[#14110F]">{g.any}</option>
                {treatments.map((x) => <option key={x} value={x} className="text-[#14110F]">{x}</option>)}
              </select>
            </label>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-9">
              <Input label={g.from} required minLength={2} value={f.buyer_name} onChange={set("buyer_name")} data-testid="gift-buyer-name-input" />
              <Input label={g.email} type="email" required value={f.buyer_email} onChange={set("buyer_email")} data-testid="gift-buyer-email-input" />
              <Input label={g.to} required minLength={2} value={f.recipient_name} onChange={set("recipient_name")} data-testid="gift-recipient-input" />
              <Input label={g.message} maxLength={120} value={f.message} onChange={set("message")} data-testid="gift-message-input" />
            </div>
            <label className="flex items-start gap-3 text-sm text-[#d9d0c4] cursor-pointer">
              <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="gift-consent-checkbox" className="mt-1 accent-[#C9A66B] w-4 h-4" />
              <span>{t.booking.consent} <Link to="/privacy" className="underline underline-offset-4 decoration-[#C9A66B] hover:text-[#C9A66B]" data-testid="gift-privacy-link">{t.booking.privacy}</Link></span>
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <button type="submit" disabled={loading} data-testid="gift-pay-btn"
                className="group inline-flex items-center justify-center gap-4 rounded-full bg-[#C9A66B] text-[#14110F] px-9 py-4 text-[0.75rem] tracking-[0.2em] uppercase font-medium transition-colors duration-500 hover:bg-[#F6F2EC] disabled:opacity-60">
                {loading ? g.paying : `${g.pay}${amount ? ` · €${amount}` : ""}`}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
              </button>
              <p className="flex items-center gap-2 text-xs text-[#b8ad9f]"><Lock className="w-3.5 h-3.5" /> {g.secure}</p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
